import {
  Close as CloseIcon,
  Link as LinkIcon,
  Place as PlaceIcon,
} from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import type { Terminal } from '@otw/models/leads/terminals'
import { getTerminalDisplayName } from '@otw/models/leads/terminals'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useTerminals } from 'hooks/terminals/useTerminals'
import PickupLocationSelector from 'views/SuperAdmin/Management/LeadManagement/components/PickupLocationSelector'
import {
  formatPickupLocationAddress,
  resolvePickupLocationById,
} from 'views/SuperAdmin/Management/LeadManagement/util/pickupLocationDisplay'
import { resolveTerminalLocation } from '@/core/terminals/terminalAddress'

type AddressMode = 'enter' | 'link'

interface TerminalFormDialogProps {
  open: boolean
  onClose: () => void
  terminal?: Terminal | null
  /** When creating from a company form, pre-select this company. */
  defaultCompanyId?: string | null
  /** Called with the new terminal id after a successful create. */
  onCreated?: (terminalId: string) => void
}

const TerminalFormDialog: React.FC<TerminalFormDialogProps> = ({
  open,
  onClose,
  terminal = null,
  defaultCompanyId = null,
  onCreated,
}) => {
  const { createTerminal, updateTerminal, isSaving, terminals } = useTerminals()
  const { companies } = useContactCompanies()
  const { pickupLocations } = usePickupLocations()
  const prevOpenRef = useRef(false)

  const [name, setName] = useState('')
  const [abbreviation, setAbbreviation] = useState('')
  const [number, setNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [addressMode, setAddressMode] = useState<AddressMode>('enter')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [linkedLocationId, setLinkedLocationId] = useState<string | null>(null)
  const [selectedCompanies, setSelectedCompanies] = useState<ContactCompany[]>([])
  const [editingLinkedAddress, setEditingLinkedAddress] = useState(false)

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current
    prevOpenRef.current = open
    if (!justOpened) return

    if (terminal) {
      setName(terminal.name ?? '')
      setAbbreviation(terminal.abbreviation ?? '')
      setNumber(terminal.number ?? '')
      setNotes(terminal.notes ?? '')
      const location = resolveTerminalLocation(terminal, pickupLocations)
      setLinkedLocationId(terminal.pickup_location_id)
      setAddress(location?.address ?? '')
      setCity(location?.city ?? '')
      setState(location?.state ?? '')
      setZip(location?.zip ?? '')
      setAddressMode('enter')
      setEditingLinkedAddress(false)
      setSelectedCompanies(
        companies.filter((c) => (c.terminalIds ?? []).includes(terminal.id)),
      )
    } else {
      setName('')
      setAbbreviation('')
      setNumber('')
      setNotes('')
      setAddress('')
      setCity('')
      setState('')
      setZip('')
      setLinkedLocationId(null)
      setAddressMode('enter')
      setEditingLinkedAddress(false)
      if (defaultCompanyId) {
        const company = companies.find((c) => c.id === defaultCompanyId)
        setSelectedCompanies(company ? [company] : [])
      } else {
        setSelectedCompanies([])
      }
    }
  }, [open, terminal, companies, pickupLocations, defaultCompanyId])

  const duplicateTerminal = useMemo(() => {
    const locationId =
      addressMode === 'link'
        ? linkedLocationId?.trim()
        : terminal?.pickup_location_id?.trim()
    if (!locationId) return null
    return (
      terminals.find(
        (t) =>
          t.id !== terminal?.id &&
          t.pickup_location_id?.trim() === locationId,
      ) ?? null
    )
  }, [addressMode, linkedLocationId, terminal, terminals])

  const linkedLocation = useMemo(
    () => resolvePickupLocationById(pickupLocations, linkedLocationId),
    [pickupLocations, linkedLocationId],
  )

  const enterAddressValid =
    address.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0 &&
    zip.trim().length > 0

  const valid =
    addressMode === 'enter'
      ? enterAddressValid
      : Boolean(linkedLocationId?.trim()) &&
        (!editingLinkedAddress || enterAddressValid)

  const dialogTitle = terminal ? 'Edit terminal' : 'Add terminal'
  const submitLabel = isSaving ? 'Saving…' : terminal ? 'Save' : 'Create'

  const handleSubmit = async () => {
    const companyIds = selectedCompanies.map((c) => c.id)

    if (terminal) {
      if (addressMode === 'link') {
        await updateTerminal({
          id: terminal.id,
          name,
          abbreviation,
          number,
          notes,
          pickup_location_id: linkedLocationId,
          ...(editingLinkedAddress
            ? { address: { address, city, state, zip } }
            : {}),
          companyIds,
        })
      } else {
        await updateTerminal({
          id: terminal.id,
          name,
          abbreviation,
          number,
          notes,
          address: { address, city, state, zip },
          companyIds,
        })
      }
    } else if (addressMode === 'link') {
      const id = await createTerminal({
        name,
        abbreviation,
        number,
        notes,
        pickup_location_id: linkedLocationId,
        ...(editingLinkedAddress
          ? { address: { address, city, state, zip } }
          : {}),
        companyIds,
      })
      onCreated?.(id)
    } else {
      const id = await createTerminal({
        name,
        abbreviation,
        number,
        notes,
        address: { address, city, state, zip },
        companyIds,
      })
      onCreated?.(id)
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
              Terminal identity
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Name"
                  fullWidth
                  size="small"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSaving}
                  placeholder="Memphis Hub"
                  autoFocus
                />
                <TextField
                  label="Abbreviation"
                  fullWidth
                  size="small"
                  value={abbreviation}
                  onChange={(e) => setAbbreviation(e.target.value)}
                  disabled={isSaving}
                  placeholder="MEM1"
                />
                <TextField
                  label="Number"
                  fullWidth
                  size="small"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  disabled={isSaving}
                  placeholder="1234"
                />
              </Stack>
              <TextField
                label="Notes"
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSaving}
                placeholder="Internal notes about this terminal (optional)"
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.8 }}
              >
                Address
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={addressMode}
                onChange={(_, value: AddressMode | null) => {
                  if (!value) return
                  setAddressMode(value)
                  setEditingLinkedAddress(false)
                }}
                disabled={isSaving}
              >
                <ToggleButton value="enter" sx={{ textTransform: 'none', px: 1.5 }}>
                  <PlaceIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Enter address
                </ToggleButton>
                <ToggleButton value="link" sx={{ textTransform: 'none', px: 1.5 }}>
                  <LinkIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Link existing
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {addressMode === 'enter' ? (
              <Stack spacing={1.5}>
                <TextField
                  label="Address"
                  required
                  fullWidth
                  size="small"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={isSaving}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="City"
                    required
                    fullWidth
                    size="small"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    disabled={isSaving}
                  />
                  <TextField
                    label="State"
                    required
                    fullWidth
                    size="small"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    disabled={isSaving}
                  />
                  <TextField
                    label="ZIP"
                    required
                    fullWidth
                    size="small"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    disabled={isSaving}
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <PickupLocationSelector
                  label="Existing address"
                  addressKind="dropoff"
                  value={linkedLocationId}
                  onChange={(id) => {
                    setLinkedLocationId(id)
                    setEditingLinkedAddress(false)
                    const loc = resolvePickupLocationById(pickupLocations, id)
                    setAddress(loc?.address ?? '')
                    setCity(loc?.city ?? '')
                    setState(loc?.state ?? '')
                    setZip(loc?.zip ?? '')
                  }}
                  disabled={isSaving}
                  showCreateButton={false}
                  showEditButton={false}
                />
                {linkedLocation ? (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setEditingLinkedAddress((v) => !v)}
                        sx={{ textTransform: 'none' }}
                      >
                        {editingLinkedAddress ? 'Cancel edit' : 'Edit this address'}
                      </Button>
                    }
                  >
                    {formatPickupLocationAddress(linkedLocation) || 'No address on file'}
                  </Alert>
                ) : null}
                {editingLinkedAddress ? (
                  <Stack spacing={1.5}>
                    <TextField
                      label="Address"
                      required
                      fullWidth
                      size="small"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      disabled={isSaving}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        label="City"
                        required
                        fullWidth
                        size="small"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={isSaving}
                      />
                      <TextField
                        label="State"
                        required
                        fullWidth
                        size="small"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        disabled={isSaving}
                      />
                      <TextField
                        label="ZIP"
                        required
                        fullWidth
                        size="small"
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                        disabled={isSaving}
                      />
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>
            )}

            {duplicateTerminal ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Another terminal already uses this address:{' '}
                <strong>{getTerminalDisplayName(duplicateTerminal)}</strong>
              </Alert>
            ) : null}
          </Box>

          <Divider />

          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 0.8, display: 'block', mb: 1 }}
            >
              Companies
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={companies}
              value={selectedCompanies}
              onChange={(_, value) => setSelectedCompanies(value)}
              disabled={isSaving}
              getOptionLabel={(option) => option.name || 'Unnamed company'}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    label={option.name || 'Unnamed company'}
                    deleteIcon={<CloseIcon />}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search companies to associate"
                  helperText="One terminal can be linked to many companies."
                />
              )}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={isSaving || !valid}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TerminalFormDialog
